import * as React from "react";
import styled from "@emotion/styled";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  BrushWrapper,
  Container,
  FadeIn,
  FlexRow,
  H1,
  H2,
  IconArrow,
  Link,
  NoBreak,
  ScreenReaderText,
  Stack,
  Text,
} from "atoms";
import { CreateAccountForm } from "features/auth";
import { GenericImage } from "features/images";
import { SiteContentFallback, SiteHeader, SiteFooter } from "features/layouts";
import { usePrefersReducedMotion } from "utils/accessibility";
import homeForArt from "assets/images/1-a-home-for-your-art.png";
import opportunitiesForArt from "assets/images/2-opportunities-for-your-art.png";
import learnAndGoFurther from "assets/images/3-learn-and-go-further.png";
import partnersLogos from "assets/images/partners-logos.jpg";
import slide1 from "assets/images/slide1-art.png";
import slide1Sm from "assets/images/slide1-art-sm.png";
import { hexToRGB } from "styles/styleUtils";
import { slides, testimonials, artists } from "../data/loggedOutContent";

const columnBreakpoint = "@media (min-width: 720px)";

function LoggedOutHomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  const onCreateAccount = () => {
    navigate("/artist-application", {
      state: { newAccount: true },
    });
  };

  return (
    <Site>
      <SiteContent>
        <ErrorBoundary
          FallbackComponent={SiteContentFallback}
          resetKeys={[location]}
          onReset={goBack}
        >
          <TopSection>
            <SiteHeader />
            <CustomHeader />
          </TopSection>

          <Section id="section1" className="Section">
            {/* <Pill1 src={pillsRed} /> */}
            <SectionContainer>
              <SectionContent>
                <SectionPrimary>
                  <SectionHeader>A Home for Your&nbsp;Art</SectionHeader>
                  <SectionCopy>
                    New work, old work, or works-in-progress&mdash;get creative
                    in organizing your Portfolio while connecting with creative
                    friends from around&nbsp;the&nbsp;world.
                  </SectionCopy>
                  <ColorButton bg="red" to="#join">
                    Join Now
                  </ColorButton>
                </SectionPrimary>
                <SectionSecondary>
                  <MainImage
                    src={homeForArt}
                    alt="A Hug artist profile behind three positive reviews from art lovers"
                  />
                </SectionSecondary>
              </SectionContent>
            </SectionContainer>
          </Section>

          <Section id="section2" className="Section">
            <SectionContainer>
              <SectionContent>
                <SectionPrimary>
                  <SectionHeader>A space for growth</SectionHeader>
                  <SectionCopy>
                    Take your art from the small screen to the global stage with
                    exclusive financial grants, international exhibitions,
                    merchandising opportunities, and&nbsp;more.
                  </SectionCopy>
                  <ScreenReaderText as="p">
                    {/* Sentence-style stats put here instead of in long, weird alt text */}
                    So far, over $160,000 in grants have been awarded and more
                    than 1000 artists have been given opportunities to display
                    their art in galleries worldwide.
                  </ScreenReaderText>
                  <ColorButton bg="yellow" to="#join">
                    Join Now
                  </ColorButton>
                </SectionPrimary>
                <SectionSecondary>
                  <Stack gap="sm">
                    <MainImageWorkedWith
                      src={opportunitiesForArt}
                      alt="Two photos from exhibitions showing Hug artists work on display"
                    />

                    <WorkedWithRow>
                      <Text
                        bold
                        size="xxxs"
                        textAlign="center"
                        style={{ margin: 0 }}
                      >
                        We work with
                        <ScreenReaderText as="span">
                          L&apos;Oreal, MasterCard, and The United Nations.
                        </ScreenReaderText>
                      </Text>
                      <PartnersLogosImage src={partnersLogos} aria-hidden />
                    </WorkedWithRow>
                  </Stack>
                </SectionSecondary>
              </SectionContent>
            </SectionContainer>
          </Section>

          <Section id="section3" className="Section">
            <SectionContainer>
              <SectionContent>
                <SectionPrimary>
                  <SectionHeader>A place to learn</SectionHeader>
                  <SectionCopy>
                    Learn at your own pace, join a live workshop, or collaborate
                    in guided courses on art, artist tools, and creative
                    entrepreneurship to bring your ideas to&nbsp;life.
                  </SectionCopy>
                  <ScreenReaderText as="p">
                    {/* Sentence-style stats put here instead of in long, weird alt text */}
                    Over one thousand creators have been mentored on one or more
                    of the fifty plus topics covered.
                  </ScreenReaderText>
                  <ColorButton bg="blue" to="#join">
                    Join Now
                  </ColorButton>
                </SectionPrimary>
                <SectionSecondary>
                  <MainImage
                    src={learnAndGoFurther}
                    alt="An image collage showing a screenshot from a live workshop and the cover of Hug's free Ultimate Artist Marketing handbook."
                  />
                </SectionSecondary>
              </SectionContent>
            </SectionContainer>
          </Section>

          <TestimonialSection id="section4" className="Section">
            <SectionContainer>
              <Stack gap="md">
                <Stack gap="sm">
                  <SectionHeader textAlign="center">
                    Hear from Our&nbsp;Artists
                  </SectionHeader>
                  <Text textAlign="center">
                    <Link to="#join" variant="primary" arrow="r">
                      Join Now
                    </Link>
                  </Text>
                </Stack>
                <Stack gap="sm">
                  <TestimonialGrid>
                    {testimonials.map(({ name, artistId, pfp, quote }) => (
                      <Testimonial key={artistId}>
                        <QuoteMark aria-hidden>&ldquo;</QuoteMark>
                        <TestimonialText textAlign="center">
                          <ScreenReaderText as="span">
                            {name} said:{" "}
                          </ScreenReaderText>
                          {quote}
                        </TestimonialText>
                        <Stack gap="xxs">
                          <H2
                            as={Text}
                            font="display"
                            textAlign="center"
                            textTransform="uppercase"
                            size="sm"
                            aria-hidden
                          >
                            {name}
                          </H2>
                          <Text
                            font="display"
                            textAlign="center"
                            textTransform="uppercase"
                          >
                            <Link
                              size="xxs"
                              to={`/artists/${artistId}`}
                              aria-label={`View profile for ${name}`}
                            >
                              View Profile
                            </Link>
                          </Text>
                        </Stack>
                        <Link
                          to={`/artists/${artistId}`}
                          aria-hidden
                          tabIndex={-1}
                          style={{ width: "min-content", margin: "0 auto" }}
                        >
                          <TestimonialImage src={pfp} aria-hidden />
                        </Link>{" "}
                      </Testimonial>
                    ))}
                  </TestimonialGrid>
                </Stack>
              </Stack>
            </SectionContainer>
          </TestimonialSection>

          <div id="join">
            <JoinSection bg="fg05" brush="1">
              <Container width="sm">
                <H2 size="h2" textAlign="center">
                  Sign Up to Join Our
                  <br />
                  Global Creative Community
                </H2>
              </Container>
              <Stack gap="lg">
                <Container width="xs">
                  <Text textAlign="center" size="xxs">
                    Have an account?{" "}
                    <Link to="/sign-in">
                      <strong>Sign in</strong>
                    </Link>
                  </Text>
                  {/* TODO:
                   *  - Move form from `CreateAccountEmail.tsx` into its own component.
                   *  - Include props for button text and onSuccess.
                   */}
                  <Stack>
                    <Box>
                      <CreateAccountForm onSuccess={onCreateAccount} />
                    </Box>

                    <Text size="xxs" textAlign="center">
                      By creating an account you agree to our{" "}
                      <NoBreak>
                        <Link to="/terms-of-service">Terms of Service</Link> and{" "}
                        <Link to="/privacy-policy">Privacy Policy</Link>.
                      </NoBreak>
                    </Text>
                  </Stack>
                </Container>

                <Credits>
                  <Container>
                    <H2 size="sm" textAlign="center" textTransform="none">
                      This page features art from the{" "}
                      <NoBreak>following artists</NoBreak>
                    </H2>
                    <Text textAlign="center">
                      {artists
                        .map<React.ReactNode>(({ name, artistId }) => (
                          <Link to={`/artists/${artistId}`} key={artistId}>
                            {name}
                          </Link>
                        ))
                        .reduce((acc, cur) => [acc, ", ", cur])}
                    </Text>
                  </Container>
                </Credits>
              </Stack>
            </JoinSection>
            <SiteFooter />
          </div>
        </ErrorBoundary>
      </SiteContent>
    </Site>
  );
}

function CustomHeader() {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  // Sets state to determine when the first image is loaded
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const imageOnLoad = () => setImageLoaded(true);

  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((old) => (old === slides.length - 1 ? 0 : old + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <CustomHeaderWrapper>
      <IntroText>
        <H1 textAlign="center">
          <TitleSmall>Celebrate &amp; Showcase</TitleSmall>
          <ScreenReaderText as="span">
            your art, paintings, ceramics, tattoos, or whatever you create.
          </ScreenReaderText>
          {prefersReducedMotion ? (
            <span aria-hidden>
              <br />
              Your Art
            </span>
          ) : (
            <SlideTextWrapper aria-hidden>
              {slides.map(({ text }, index) => (
                <SlideText key={text} isActive={index === currentIndex}>
                  {text}
                </SlideText>
              ))}
            </SlideTextWrapper>
          )}
        </H1>

        <Stack>
          <ContainedText textAlign="center">
            Join our 20,000-strong creative community in sharing your craft and
            growing it through one-of-a-kind opportunities &amp; education.
          </ContainedText>
          <Text textAlign="center">
            <ColorButton
              to="#join"
              arrow="r"
              variant="secondary"
              size="sm"
              bg="white"
            >
              Join Now
            </ColorButton>
          </Text>
        </Stack>

        {!prefersReducedMotion && (
          <SlideIndicatorRow aria-hidden gap="6px" justifyContent="center">
            {slides.map(({ text }, index) => (
              <SlideIndicator key={text} isActive={index === currentIndex} />
            ))}
          </SlideIndicatorRow>
        )}
      </IntroText>

      <SlideImagesWrapper aria-hidden>
        {prefersReducedMotion ? (
          <StaticImageWrapper>
            <SlideImageElement src={slide1Sm} size="sm" />
            <SlideImageElement src={slide1} size="lg" />
          </StaticImageWrapper>
        ) : (
          slides.map(({ imgLg, imgSm }, index) => (
            <SlideImage
              key={imgSm}
              isActive={index === currentIndex}
              loading={index === 0 ? "eager" : "lazy"}
              imgSm={imgSm}
              imgLg={imgLg}
              imageOnLoad={index === 0 ? imageOnLoad : undefined}
              imageLoaded={index === 0 || imageLoaded}
            />
          ))
        )}
      </SlideImagesWrapper>

      <ScrollText
        font="display"
        textTransform="uppercase"
        aria-hidden
        tabIndex={-1}
      >
        Scroll to Learn More <DownArrow dir="d" />
      </ScrollText>
    </CustomHeaderWrapper>
  );
}
const SlideSwapper = styled.div<{ isActive: boolean }>(({ isActive }) => ({
  opacity: isActive ? 1 : 0,
  transitionProperty: "opacity, transform",
  zIndex: isActive ? 1 : 0,
}));

const SlideTextWrapper = styled.span({
  display: "block",
  position: "relative",
  height: "1em",
  width: "100%",
});

const SlideText = styled(SlideSwapper)<{ isActive: boolean }>(
  ({ isActive }) => ({
    position: "absolute",
    textAlign: "center",
    left: "50%",
    width: "90vw",
    whiteSpace: "nowrap",
    transform: isActive
      ? "translate(-50%, 0)"
      : "translate(calc(-50% - .5em), 0)",
    transitionDelay: isActive ? "0s" : "0s, 1s",
    transitionDuration: isActive ? "1s" : ".5s",
  }),
);

const SlideIndicatorRow = styled(FlexRow)(({ theme }) => ({
  padding: 6,
  borderRadius: theme.borderRadius.round,
  backgroundColor: hexToRGB(theme.colors.fg, 0.66),
  width: "max-content",
  margin: "0 auto",
  position: "relative",
  top: "calc(.5em + 2vmin)",
}));

const SlideIndicator = styled(SlideSwapper)<{ isActive: boolean }>(
  ({ isActive, theme }) => ({
    backgroundColor: "currentColor",
    borderRadius: theme.borderRadius.round,
    height: 5,
    opacity: isActive ? 0.9 : 0.35,
    transition: "1s ease all",
    width: 5,
  }),
);

type SlideImageProps = {
  imgSm: string;
  imgLg: string;
  isActive: boolean;
  imageOnLoad?: VoidFunction;
  imageLoaded?: boolean;
  loading: "eager" | "lazy";
};

function SlideImage({
  imgSm,
  imgLg,
  isActive,
  imageOnLoad,
  imageLoaded,
  loading,
}: SlideImageProps) {
  return imageLoaded ? (
    <ImageSwapper isActive={isActive}>
      <SlideImageElement
        src={imgSm}
        size="sm"
        onLoad={imageOnLoad}
        loading={loading}
      />
      <SlideImageElement
        src={imgLg}
        size="lg"
        onLoad={imageOnLoad}
        loading={loading}
      />
    </ImageSwapper>
  ) : null;
}

const SlideImagesWrapper = styled.span({
  position: "relative",
  flex: "5 0 auto",
});

const ImageSwapper = styled(SlideSwapper)<{ isActive: boolean }>(
  ({ isActive }) => ({
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    transitionDelay: "0s",
    transitionDuration: "1s",
    transform: isActive
      ? "translateY(0) scale(1)"
      : "translateY(1rem) scale(.95)",
  }),
);

const StaticImageWrapper = styled.div({
  position: "absolute",
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
});

const SlideImageElement = styled(GenericImage)<{ size: "sm" | "lg" }>(
  ({ size, theme }) => ({
    display: size === "sm" ? "block" : "none",
    paddingTop: "2rem",
    [theme.breakpoints.desktop]: {
      display: size === "lg" ? "block" : "none",
      maxWidth: 1800,
      marginLeft: "auto",
      marginRight: "auto",
      objectFit: "contain",
      objectPosition: "bottom",
      paddingTop: 0,
    },
    objectPosition: "top",
    position: "absolute",
    bottom: 0,
    left: "-1rem",
    right: "-1rem",
    maxWidth: "calc(100% + 2rem)",
    width: "calc(100% + 2rem)",
    "@media (min-width: 1600px)": {
      maxWidth: "calc(100% - 5vw)",
    },
    height: "100%",
    img: {
      height: "100%",
      objectPosition: "top",
      objectFit: "cover",
      [columnBreakpoint]: {
        objectFit: "contain",
        objectPosition: "bottom",
        "@media (min-aspect-ratio: 1.33/1)": {
          objectFit: "cover",
          objectPosition: "top",
        },
        "@media (min-aspect-ratio: 1/1.5)": {
          objectFit: "cover",
          objectPosition: "top",
        },
      },
    },
  }),
);

const CustomHeaderWrapper = styled(BrushWrapper)(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "2fr 3fr",
  overflow: "hidden",
  width: "100%",
  flex: "1 0 auto",
  [theme.breakpoints.tablet]: {
    display: "flex",
    flexDirection: "column",
  },
}));
CustomHeaderWrapper.defaultProps = {
  bg: "fg",
  brush: "none",
};

const IntroText = styled(Container)(({ theme }) => ({
  alignSelf: "center",
  [theme.breakpoints.tablet]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flex: "1 0 auto",
  },
}));

const TitleSmall = styled.small({
  fontSize: ".5em",
});

const ContainedText = styled(Text)(({ theme }) => ({
  maxWidth: "50ch",
  marginLeft: "auto",
  marginRight: "auto",
  [theme.breakpoints.desktopMedium]: {
    fontSize: theme.fontSizes.md,
  },
}));

const Site = styled.div({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  scrollBehavior: "smooth",
});

const SiteContent = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  flex: "1 1 auto",
  overflow: "hidden",
}));

const Section = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "stretch",
  padding: "5em 0 2em 0",
  position: "relative",
  [columnBreakpoint]: {
    padding: "5em 0",
  },
  "&>*": {
    flex: 1,
  },
});

const SectionContent = styled.div({
  display: "grid",
  gridTemplateRows: "1fr auto",
  gridTemplateAreas: `"primary" "secondary"`,
  alignItems: "center",
  justifySelf: "center",
  maxWidth: 1600,
  margin: "0 auto",
  width: "100%",
  [columnBreakpoint]: {
    gap: "2rem",
    gridTemplateRows: "100%",
    gridTemplateColumns: "2fr 3fr",
    gridTemplateAreas: `"primary secondary"`,
    ".Section:nth-of-type(odd) &": {
      gridTemplateColumns: "3fr 2fr",
      gridTemplateAreas: `"secondary primary"`,
    },
  },
});

const JoinSection = styled(BrushWrapper)({
  marginTop: "4rem",
  padding: "4rem 0",
  "&::before": {
    backgroundSize: "200vmax",
    zIndex: -1,
  },
});

const SectionPrimary = styled.div(({ theme }) => ({
  gridArea: "primary",
  padding: "2rem 0 0",
  [theme.breakpoints.tablet]: {
    maxWidth: 400,
    padding: "2rem 0",
    justifySelf: "center",
  },
}));

const SectionSecondary = styled(FadeIn)(({ theme }) => ({
  color: theme.colors.fg,
  gridArea: "secondary",
  padding: "1.5rem 0",
  [theme.breakpoints.tablet]: {
    alignSelf: "stretch",
    display: "grid",
    placeItems: "center",
    padding: 0,
    maxHeight: "90vh",
    position: "relative",
  },
}));

const MainImage = styled(GenericImage)(({ theme }) => ({
  objectFit: "contain",
  objectPosition: "center",
  height: "100%",
  marginLeft: "auto",
  marginRight: "auto",
  [theme.breakpoints.tablet]: {
    maxWidth: 666,
  },
  img: {
    objectFit: "contain",
    objectPosition: "center",
    height: "100%",
    [theme.breakpoints.tablet]: {
      maxHeight: "90vh",
    },
  },
}));
MainImage.defaultProps = {
  loading: "lazy",
};
const MainImageWorkedWith = styled(MainImage)(({ theme }) => ({
  [theme.breakpoints.tablet]: {
    maxWidth: "70vw",
    img: {
      maxHeight: "calc(90vh - 4rem)",
    },
  },
}));

const TopSection = styled.div(({ theme }) => ({
  height: "100svh",
  "@supports (height: 100svh)": { height: "100svh" },
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "stretch",
  position: "relative",
  backgroundColor: theme.colors.fg,
}));

const ScrollText = styled(Text)(({ theme }) => ({
  border: 0,
  bottom: "env(safe-area-inset-bottom)",
  color: theme.colors.bg,
  cursor: "default",
  fontSize: 11,
  left: "50%",
  padding: "1.5em 4em",
  position: "absolute",
  textShadow:
    "0px 0px 1px rgb(38 38 38), 0px 0px 5px rgb(38 38 38), 0px 0px 10px rgb(38 38 38), 0px 0px 15px rgb(38 38 38), 0px 0px 20px rgb(38 38 38), 0px 0px 20px rgb(38 38 38), 0px 0px 25px rgb(38 38 38), 0px 0px 25px rgb(38 38 38), 0px 0px 35px rgb(38 38 38), 0px 0px 35px rgb(38 38 38)",
  transform: "translate(-50%, 1em)",
  transition: ".2s ease",
  transitionProperty: "opacity, transform",
  whiteSpace: "nowrap",
  zIndex: 20,
  "&:focus, &:focus-visible, &:hover": {
    color: theme.colors.bg,
    transform: "translate(-50%, 1em)",
    border: 0,
  },
  ".Section &": {
    color: theme.colors.fg,
    bottom: "1rem",
    fontSize: 30,
    opacity: 0.33,
    "&:hover, &:focus-visible": {
      opacity: 1,
      transform: "translate(-50%, 0)",
    },
  },
}));

const DownArrow = styled(IconArrow)({
  width: 12,
  height: 12,
  position: "relative",
  top: 2,
  left: 2,
});

const TestimonialSection = styled(Section)({
  paddingBottom: "0 !important",
});

const TestimonialGrid = styled.div(({ theme }) => ({
  [theme.breakpoints.tablet]: {
    display: "grid",
    gap: `calc(${theme.spacing.gap} / 2) ${theme.spacing.gap}`,
    gridTemplateColumns: "repeat(auto-fit, minmax(40%, 1fr))",
  },
  [theme.breakpoints.desktop]: {
    gridTemplateColumns: "repeat(auto-fit, minmax(25%, 1fr))",
  },
}));

const Testimonial = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  paddingBottom: 0,
  marginBottom: 60,
  overflow: "visible",
  [theme.breakpoints.maxTablet]: {
    // Show only 3 on mobile
    "&:nth-of-type(4),&:nth-of-type(5),&:nth-of-type(6)": {
      display: "none",
    },
    maxWidth: 400,
    marginLeft: "auto",
    marginRight: "auto",
  },
  [theme.breakpoints.maxDesktop]: {
    // Show only 4 on tablet
    "&:nth-of-type(5),&:nth-of-type(6)": {
      display: "none",
    },
  },
}));

const TestimonialImage = styled(GenericImage)(({ theme }) => ({
  borderRadius: "99px 99px 4px 4px",
  overflow: "hidden",
  width: 100,
  maxWidth: 100,
  height: 120,
  boxShadow: theme.boxShadow.dark,
  margin: "0 auto",
  marginTop: "-20px !important",
  transform: "translateY(30px)",
}));

const QuoteMark = styled(Text)({
  opacity: 0.4,
  lineHeight: ".25em",
  paddingTop: ".5em",
  marginBottom: ".1em",
  fontSize: 50,
});
QuoteMark.defaultProps = {
  font: "display",
  textAlign: "center",
};

const TestimonialText = styled(Text)(({ theme }) => ({
  flex: 1,
  fontSize: theme.fontSizes.sm,
  marginBottom: "1.5rem",
  [theme.breakpoints.tablet]: {
    fontSize: theme.fontSizes.xs,
  },
}));

const WorkedWithRow = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyItems: "center",
  flexWrap: "wrap",
  gap: "0 10px",
  itemsFlex: "0 1 auto",
  justifyContent: "center",
  [theme.breakpoints.tablet]: {
    flexDirection: "row",
  },
}));

const PartnersLogosImage = styled(GenericImage)(({ theme }) => ({
  height: "auto",
  maxWidth: 275,
  flex: "1 1 auto",
  mixBlendMode: "multiply",
  [theme.breakpoints.tablet]: {
    flex: "0 0 275px",
  },
  img: {
    maxWidth: 275,
    aspectRatio: "275 / 42",
  },
}));

const ColorButton = styled(Link)<{
  bg: "white" | "black" | "blue" | "yellow" | "red";
}>(({ bg = "black", theme }) => ({
  fontSize: theme.fontSizes.sm,
  [theme.breakpoints.desktopLarge]: {
    fontSize: theme.fontSizes.md,
  },
  "&&": {
    color: theme.colors.fg,
    backgroundColor: theme.colors.bg,
    ...(bg === "white" && {
      color: theme.colors.fg,
      backgroundColor: theme.colors.bg,
      borderColor: theme.colors.bg,
      "&:hover, &:focus-visible": {
        color: theme.colors.bg,
        backgroundColor: theme.colors.fg,
        borderColor: theme.colors.bg,
        opacity: 1,
      },
    }),
    ...(bg === "red" && {
      color: theme.colors.bg,
      // off-brand, but accessible bg color for white text
      backgroundColor: "#C5371E",
      borderColor: "#C5371E",
      "&:hover, &:focus-visible": {
        color: theme.colors.bg,
        backgroundColor: theme.colors.fg,
        borderColor: theme.colors.fg,
      },
    }),
    ...(bg === "yellow" && {
      color: theme.colors.fg,
      backgroundColor: theme.colors.accent3,
      borderColor: theme.colors.accent3,
      "&:hover, &:focus-visible": {
        color: theme.colors.bg,
        backgroundColor: theme.colors.fg,
        borderColor: theme.colors.fg,
      },
    }),
    ...(bg === "blue" && {
      color: theme.colors.fg,
      backgroundColor: theme.colors.accent1,
      borderColor: theme.colors.accent1,
      "&:hover, &:focus-visible": {
        color: theme.colors.bg,
        backgroundColor: theme.colors.fg,
        borderColor: theme.colors.fg,
      },
    }),
  },
}));
ColorButton.defaultProps = {
  variant: "primary",
  size: "xs",
  arrow: "r",
};

const SectionHeader = styled(H2)(({ theme }) => ({
  letterSpacing: 0,
  fontSize: theme.fontSizes.xxl,
  flex: "0 0 auto",
  [theme.breakpoints.tablet]: {
    fontSize: theme.fontSizes.xl,
  },
  [columnBreakpoint]: {
    fontSize: theme.fontSizes.xxl,
  },
  [theme.breakpoints.desktopLarge]: {
    fontSize: theme.fontSizes.xxxl,
  },
}));

const SectionCopy = styled(Text)(({ theme }) => ({
  fontSize: theme.fontSizes.sm,
  [theme.breakpoints.desktopLarge]: {
    fontSize: theme.fontSizes.md,
  },
}));

const SectionContainer = styled(Container)({
  display: "grid",
  placeItems: "center",
  "@media (min-width: 600px) and (max-width: 720px)": {
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
  },
});
SectionContainer.defaultProps = {
  width: "lg",
};

const Credits = styled.div(({ theme }) => ({
  fontSize: theme.fontSizes.xxs,
  marginBottom: "3rem",
  ul: {
    display: "inline",
  },
  li: {
    display: "inline",
    whiteSpace: "nowrap",
  },
}));

export { LoggedOutHomePage };
